"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { Check, Minus, Pencil, Plus, Trash2, X } from "lucide-react"
import type { PantryItem } from "@bitebalance/contracts"
import { useI18n } from "@/i18n"
import { unitLabel } from "@/lib/units"
import { EmptyState } from "@/components/state"

export interface InventoryListProps {
  items: PantryItem[]
  /** When provided, each card offers quantity editing (steppers + direct input). Quantity is in the item's canonical unit.
   *  expiryDate, when passed, sets/clears the card's expiry alongside — omit the arg entirely to leave expiry untouched. */
  onUpdateQuantity?: (itemId: string, quantity: number, expiryDate?: string | null) => Promise<void>
  /** When provided, each card offers a remove action. */
  onRemove?: (itemId: string) => Promise<void>
}

/** Stepper increment per unit — one tap should mean a real-world nudge. */
function stepFor(unit: string): number {
  const u = unit.toLowerCase()
  if (u === "g" || u === "gram" || u === "ml") return 50
  return 1
}

/** Whole calendar days from today (UTC date-only, matching how expiryDate is already parsed below). */
function daysUntil(iso: string): number {
  const today = new Date()
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  const target = new Date(`${iso}T00:00:00Z`).getTime()
  return Math.round((target - todayUtc) / 86_400_000)
}

/** Kitchen redesign (2026-09-14): the same subtle, color-only urgency cue as mobile's row
 *  "spine" (pantry.tsx spineFor) — a quiet left-edge accent, not a badge or warning color,
 *  so Kitchen keeps reading as a calm product rather than an expiry-management tool. */
function expiryAccentClass(days: number | null): string {
  if (days == null) return ""
  if (days <= 1) return "border-l-4 border-l-coral"
  if (days <= 3) return "border-l-4 border-l-gold"
  return ""
}

/**
 * Pantry inventory — 2026-08-30 owner redesign ("so old school"): plain
 * text rows with Edit/Remove links became a responsive card grid. Each
 * card: the item name, a tap-to-type quantity with −/+ steppers (50 g or
 * 1-count nudges, saved on a short debounce so tapping +++ is one write),
 * a quiet trash icon, expiry pill, and staggered entrances. Amy writes to
 * the same inventory through her pantry tools — edits here and edits in
 * chat land in one place.
 */
export function InventoryList({ items, onUpdateQuantity, onRemove }: InventoryListProps) {
  const { t, locale } = useI18n()
  const reduce = useReducedMotion()
  const [typingId, setTypingId] = useState<string | null>(null)
  const [draftQuantity, setDraftQuantity] = useState("")
  const [expiryEditId, setExpiryEditId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [errorKey, setErrorKey] = useState<"editError" | "removeError" | null>(null)
  // Optimistic stepper state: itemId -> pending canonical amount.
  const [pending, setPending] = useState<Record<string, number>>({})
  const debounceRef = useRef<Record<string, number>>({})

  if (items.length === 0) {
    return <EmptyState title={t("views.pantry.emptyTitle")} body={t("views.pantry.emptyBody")} />
  }

  function nudge(item: PantryItem, direction: 1 | -1) {
    if (!onUpdateQuantity) return
    const step = stepFor(item.quantity.canonical.unit)
    const current = pending[item.id] ?? item.quantity.canonical.amount
    const next = Math.max(0, current + direction * step)
    setPending(prev => ({ ...prev, [item.id]: next }))
    setErrorKey(null)
    window.clearTimeout(debounceRef.current[item.id])
    debounceRef.current[item.id] = window.setTimeout(() => {
      void (async () => {
        setBusyId(item.id)
        try {
          await onUpdateQuantity(item.id, next)
        } catch {
          setErrorKey("editError")
          setPending(prev => {
            const rest = { ...prev }
            delete rest[item.id]
            return rest
          })
        } finally {
          setBusyId(null)
        }
      })()
    }, 600)
  }

  async function saveTyped(item: PantryItem) {
    if (!onUpdateQuantity) return
    const parsed = Number(draftQuantity.replace(",", "."))
    if (!Number.isFinite(parsed) || parsed < 0) return
    setBusyId(item.id)
    setErrorKey(null)
    try {
      await onUpdateQuantity(item.id, parsed)
      setPending(prev => ({ ...prev, [item.id]: parsed }))
      setTypingId(null)
    } catch {
      setErrorKey("editError")
    } finally {
      setBusyId(null)
    }
  }

  async function saveExpiry(item: PantryItem, expiryDate: string | null) {
    if (!onUpdateQuantity) return
    setBusyId(item.id)
    setErrorKey(null)
    try {
      await onUpdateQuantity(
        item.id,
        pending[item.id] ?? item.quantity.canonical.amount,
        expiryDate
      )
      setExpiryEditId(null)
    } catch {
      setErrorKey("editError")
    } finally {
      setBusyId(null)
    }
  }

  async function remove(item: PantryItem) {
    if (!onRemove) return
    setBusyId(item.id)
    setErrorKey(null)
    try {
      await onRemove(item.id)
    } catch {
      setErrorKey("removeError")
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {errorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(`views.pantry.${errorKey}`)}
        </p>
      )}
      <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const shownAmount = pending[item.id] ?? item.quantity.canonical.amount
          const isTyping = typingId === item.id
          const days = item.expiryDate ? daysUntil(item.expiryDate) : null
          return (
            <motion.li
              key={item.id}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduce ? 0 : Math.min(i * 0.04, 0.3), duration: 0.25 }}
              // `min-w-0`: a grid item defaults to `min-width: auto`, so at 390px the card
              // could not shrink below its widest child and hung ~11px past the viewport.
              className={`flex min-w-0 flex-col gap-2.5 rounded-bb-xl border border-line bg-paper p-3.5 shadow-sm transition-shadow hover:shadow-md ${expiryAccentClass(days)}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 truncate font-semibold text-ink capitalize">
                  {item.displayName}
                </p>
                {onRemove && (
                  <button
                    type="button"
                    aria-label={t("views.pantry.removeButton")}
                    disabled={busyId === item.id}
                    onClick={() => void remove(item)}
                    className="shrink-0 rounded-bb-sm p-1 text-muted-foreground transition-colors hover:text-danger"
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                )}
              </div>

              {isTyping ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    autoFocus
                    value={draftQuantity}
                    onChange={e => setDraftQuantity(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") void saveTyped(item)
                      if (e.key === "Escape") setTypingId(null)
                    }}
                    aria-label={t("views.pantry.editQuantityLabel", {
                      unit: unitLabel(item.quantity.canonical.unit, t),
                    })}
                    className="w-24 rounded-bb-md border border-green bg-paper px-2 py-1.5 text-sm text-ink outline-none focus:ring-2 focus:ring-lime/40"
                  />
                  <span className="text-xs text-ink-2">
                    {unitLabel(item.quantity.canonical.unit, t)}
                  </span>
                  <button
                    type="button"
                    aria-label={t("views.pantry.editSave")}
                    onClick={() => void saveTyped(item)}
                    className="rounded-bb-sm p-1.5 text-green hover:bg-green/10"
                  >
                    <Check className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={t("views.pantry.editCancel")}
                    onClick={() => setTypingId(null)}
                    className="rounded-bb-sm p-1.5 text-muted-foreground hover:bg-cream"
                  >
                    <X className="size-4" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    {onUpdateQuantity && (
                      <button
                        type="button"
                        aria-label="−"
                        onClick={() => nudge(item, -1)}
                        className="flex size-8 items-center justify-center rounded-bb-md border border-line text-ink-2 transition-colors hover:border-green hover:text-ink"
                      >
                        <Minus className="size-3.5" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (!onUpdateQuantity) return
                        setDraftQuantity(String(shownAmount))
                        setTypingId(item.id)
                      }}
                      className="min-w-[4.5rem] rounded-bb-md px-2 py-1 text-center text-sm font-semibold text-ink transition-colors hover:bg-cream"
                    >
                      {item.uncertain && `${t("views.pantry.uncertainPrefix")} `}
                      {new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-DE").format(
                        shownAmount
                      )}{" "}
                      {unitLabel(item.quantity.canonical.unit, t)}
                      {onUpdateQuantity && (
                        <Pencil
                          className="ml-1 inline size-3 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                    {onUpdateQuantity && (
                      <button
                        type="button"
                        aria-label="+"
                        onClick={() => nudge(item, 1)}
                        className="flex size-8 items-center justify-center rounded-bb-md border border-line text-ink-2 transition-colors hover:border-green hover:text-ink"
                      >
                        <Plus className="size-3.5" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {onUpdateQuantity &&
                (expiryEditId === item.id ? (
                  <input
                    type="date"
                    autoFocus
                    disabled={busyId === item.id}
                    defaultValue={item.expiryDate ?? ""}
                    min={new Date().toISOString().slice(0, 10)}
                    aria-label={t("views.pantry.expiryOptionalLabel")}
                    onChange={e => void saveExpiry(item, e.target.value || null)}
                    onBlur={() => setExpiryEditId(null)}
                    className="w-40 self-start rounded-bb-md border border-green bg-paper px-2 py-1 text-xs text-ink outline-none focus:ring-2 focus:ring-lime/40"
                  />
                ) : (
                  <button
                    type="button"
                    disabled={busyId === item.id}
                    onClick={() => setExpiryEditId(item.id)}
                    className={
                      item.expiryDate
                        ? "self-start rounded-bb-full bg-gold/15 px-2 py-0.5 text-[11px] font-medium text-ink-2 transition-colors hover:bg-gold/25"
                        : "self-start text-[11px] font-semibold text-green underline"
                    }
                  >
                    {item.expiryDate
                      ? t("views.pantry.expiryLabel", {
                          date: new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE").format(
                            new Date(`${item.expiryDate}T00:00:00Z`)
                          ),
                        })
                      : t("views.pantry.addExpiryButton")}
                  </button>
                ))}
              {!onUpdateQuantity && item.expiryDate && (
                <span className="self-start rounded-bb-full bg-gold/15 px-2 py-0.5 text-[11px] font-medium text-ink-2">
                  {t("views.pantry.expiryLabel", {
                    date: new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE").format(
                      new Date(`${item.expiryDate}T00:00:00Z`)
                    ),
                  })}
                </span>
              )}
            </motion.li>
          )
        })}
      </ul>
    </div>
  )
}
